import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/shared";
import { fmtNOK } from "@/lib/labels";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { InvoiceBasisTab, ProfitabilityTab, TimeReportsTab, AgreementsOverviewTab } from "@/components/TimeReports";

const tip = { contentStyle: { background: "#18181b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 } };
const curMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className="font-display text-2xl mt-2">{value}</p>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  const [dash, setDash] = useState(null);
  const [loc, setLoc] = useState(null);
  useEffect(() => {
    api.get("/reports/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/reports/by-location").then((r) => setLoc(r.data)).catch(() => {});
  }, []);
  if (!s || !dash) return <p className="text-muted-foreground">Laster…</p>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total MRR" value={fmtNOK(s.total_mrr)} />
        <Stat label="Total ARR" value={fmtNOK(s.total_arr)} />
        <Stat label="Aktive kunder" value={s.total_customers} />
        <Stat label="Tapte/avvist" value={s.lost_customers} />
        <Stat label="Høyrisikokunder" value={s.high_risk_customers} />
        <Stat label="Uten oppdragsansvarlig" value={s.missing_engagement_manager} />
        <Stat label="Forfalte oppgaver" value={s.overdue_tasks ?? 0} />
        <Stat label="Forfalte frister (compliance)" value={s.overdue_compliance_tasks ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display text-lg mb-4">MRR per avdeling</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dash.mrr_by_department}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="name" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip {...tip} formatter={(v) => fmtNOK(v)} />
              <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display text-lg mb-4">MRR per kundeansvarlig</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dash.mrr_by_manager}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="name" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip {...tip} formatter={(v) => fmtNOK(v)} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {loc && loc.locations && loc.locations.length > 0 && (
        <div className="mt-6" data-testid="location-report">
          <h3 className="font-display text-lg mb-4">Rapport per lokasjon</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {loc.locations.map((l) => (
              <div key={l.location} className="bg-card border border-border rounded-xl p-5" data-testid={`location-card-${l.location}`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-display text-lg">{l.location}</h4>
                  <span className="text-xs label-caps text-muted-foreground">{l.employees} ansatte · {l.capacity} t/uke</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div><p className="label-caps">Kunder</p><p className="font-display text-xl mt-1">{l.customers}</p></div>
                  <div><p className="label-caps">MRR</p><p className="font-display text-xl mt-1">{fmtNOK(l.mrr)}</p></div>
                  <div><p className="label-caps">ARR</p><p className="font-display text-xl mt-1">{fmtNOK(l.arr)}</p></div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-left">{["Avdeling", "Kunder", "MRR", "Ansatte"].map((h) => <th key={h} className="py-2 label-caps">{h}</th>)}</tr></thead>
                  <tbody>
                    {l.departments.map((d) => (
                      <tr key={d.name} className="border-b border-border/50">
                        <td className="py-2">{d.name}</td>
                        <td className="py-2 text-muted-foreground">{d.customers}</td>
                        <td className="py-2 text-muted-foreground">{fmtNOK(d.mrr)}</td>
                        <td className="py-2 text-muted-foreground">{d.employees}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const isManager = user && ["admin", "management"].includes(user.role);
  const [month, setMonth] = useState(curMonth());

  if (!isManager) {
    return (
      <Page>
        <PageHeader title="Rapporter" subtitle="MRR/ARR, kundevekst og risiko" testid="reports-header" />
        <Overview />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title="Rapporter" subtitle="MRR/ARR, fakturagrunnlag, lønnsomhet og timerapporter" testid="reports-header">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-secondary border-border" data-testid="reports-month" />
      </PageHeader>
      <Tabs defaultValue="oversikt" className="w-full">
        <TabsList className="bg-secondary/50 border border-border mb-6">
          <TabsTrigger value="oversikt" data-testid="tab-oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="faktura" data-testid="tab-faktura">Fakturagrunnlag</TabsTrigger>
          <TabsTrigger value="lonnsomhet" data-testid="tab-lonnsomhet">Lønnsomhet</TabsTrigger>
          <TabsTrigger value="avtaler" data-testid="tab-avtaler">Avtaler</TabsTrigger>
          <TabsTrigger value="timerapporter" data-testid="tab-timerapporter">Timerapporter</TabsTrigger>
        </TabsList>
        <TabsContent value="oversikt"><Overview /></TabsContent>
        <TabsContent value="faktura"><InvoiceBasisTab month={month} /></TabsContent>
        <TabsContent value="lonnsomhet"><ProfitabilityTab month={month} /></TabsContent>
        <TabsContent value="avtaler"><AgreementsOverviewTab /></TabsContent>
        <TabsContent value="timerapporter"><TimeReportsTab /></TabsContent>
      </Tabs>
    </Page>
  );
}
